import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Menu, Bell, ChevronDown, Home, BarChart2, MapPin,
  Users, Tag, LogOut, Settings, User, X, PanelLeftClose, PanelLeftOpen,
  Building2, Car, DollarSign, Clock, ShieldCheck,
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  breadcrumbs?: { label: string; path?: string }[];
}

const SIDEBAR_KEY = 'vilotid_sidebar_collapsed';

const Layout: React.FC<LayoutProps> = ({ children, title, breadcrumbs = [] }) => {
  const { user, role, logout, isSuperAdmin, isCompanyAdmin, isDriver } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === '1');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);

  // Role-conditional nav items
  const adminNavItems = [
    { name: 'Dashboard',     path: '/dashboard',     icon: <Home className="w-5 h-5" /> },
    { name: 'Tachograph',    path: '/tachograph',    icon: <BarChart2 className="w-5 h-5" /> },
    { name: 'Trips',         path: '/trips',         icon: <MapPin className="w-5 h-5" /> },
    { name: 'Drivers',       path: '/drivers',       icon: <Users className="w-5 h-5" /> },
    { name: 'Vehicles',      path: '/vehicles',      icon: <Car className="w-5 h-5" /> },
    { name: 'Active Offers', path: '/active-offers', icon: <Tag className="w-5 h-5" /> },
  ];

  const companyAdminExtra = [
    { name: 'Company Settings', path: '/company-settings', icon: <Building2 className="w-5 h-5" /> },
  ];

  const superAdminExtra = [
    { name: 'Inspektionspanel', path: '/gov-panel',  icon: <ShieldCheck className="w-5 h-5" /> },
    { name: 'Companies',        path: '/companies',  icon: <Building2 className="w-5 h-5" /> },
  ];

  const driverNavItems = [
    { name: 'My Portal',  path: '/driver-portal', icon: <Home className="w-5 h-5" /> },
    { name: 'My Trips',   path: '/driver-portal', icon: <Car className="w-5 h-5" /> },
    { name: 'My Income',  path: '/driver-portal', icon: <DollarSign className="w-5 h-5" /> },
    { name: 'My Shift',   path: '/driver-portal', icon: <Clock className="w-5 h-5" /> },
  ];

  const navItems = isDriver
    ? driverNavItems
    : isSuperAdmin
      ? [...adminNavItems, ...superAdminExtra]
      : isCompanyAdmin
        ? [...adminNavItems, ...companyAdminExtra]
        : adminNavItems;

  const handleLogout = () => { logout(); navigate('/login'); };

  const toggleCollapse = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      return next;
    });
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQ.trim()) {
      navigate(`/drivers?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchQ('');
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const sidebarW = collapsed ? 72 : 256;

  // Role badge in sidebar
  const roleBadge = { superadmin: 'Gov Admin', admin: 'Company Admin', operator: 'Operator', driver: 'Driver' }[role ?? 'operator'] ?? 'Operator';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      {mobileOpen && (
        <div className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)} />
      )}

      {/* ══ SIDEBAR ══ */}
      <aside
        className={`fixed top-0 left-0 z-30 h-full flex flex-col transition-all duration-300 ease-in-out overflow-hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ width: sidebarW, backgroundColor: 'var(--color-sidebar)' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-5 border-b border-white/10 shrink-0">
          <Link to={isDriver ? '/driver-portal' : '/dashboard'} className="flex items-center gap-3 min-w-0 overflow-hidden">
            <div className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #06B6D4, #0891B2)' }}>
              <img src="/favicon.svg" alt="Vilotid" className="h-5 w-5" />
            </div>
            {!collapsed && (
              <span className="text-xl font-bold tracking-wide text-white truncate"
                style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                VILOTID
              </span>
            )}
          </Link>
          <button onClick={toggleCollapse} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden lg:flex p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition shrink-0">
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
          <button onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-white/40 hover:text-white transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Role badge */}
        {!collapsed && (
          <div className="px-5 pt-4 pb-2 shrink-0">
            <span className="text-[10px] font-bold uppercase tracking-widest text-electric-400">{roleBadge}</span>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 pt-1 space-y-1 overflow-y-auto overflow-x-hidden">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link key={`${item.path}-${item.name}`} to={item.path}
                onClick={() => setMobileOpen(false)}
                title={collapsed ? item.name : undefined}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 group relative
                  ${collapsed ? 'justify-center' : ''}
                  ${active ? 'text-white' : 'text-white/55 hover:text-white/90 hover:bg-white/8'}`}
                style={active ? {
                  background: 'linear-gradient(135deg, rgba(6,182,212,0.20) 0%, rgba(8,145,178,0.12) 100%)',
                  borderLeft: collapsed ? 'none' : '3px solid #06B6D4',
                  boxShadow: 'inset 0 0 20px rgba(6,182,212,0.06)',
                } : { borderLeft: collapsed ? 'none' : '3px solid transparent' }}
              >
                {collapsed && active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-electric-400 rounded-r" />
                )}
                <span className={`shrink-0 ${active ? 'text-electric-400' : 'text-white/40 group-hover:text-white/70'}`}>
                  {item.icon}
                </span>
                {!collapsed && item.name}
                {!collapsed && active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-electric-400" />}
              </Link>
            );
          })}
        </nav>

        {/* Bottom user row */}
        <div className={`p-3 border-t border-white/10 shrink-0 ${collapsed ? 'flex justify-center' : ''}`}>
          {collapsed ? (
            <div className="w-9 h-9 rounded-full bg-electric-500 flex items-center justify-center ring-2 ring-white/20 text-white font-bold text-sm"
              title={user?.name ?? 'User'}>
              {(user?.name ?? 'U').charAt(0).toUpperCase()}
            </div>
          ) : (
            <div className="flex items-center gap-3 px-1">
              <div className="w-9 h-9 rounded-full bg-electric-500 flex items-center justify-center ring-2 ring-white/20 text-white font-bold text-sm shrink-0">
                {(user?.name ?? 'U').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white/90 truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-white/40 truncate">{user?.email || ''}</p>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ══ MAIN ══ */}
      <div className="flex flex-col min-h-screen transition-all duration-300"
        style={{ marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024 ? sidebarW : 0 }}>

        {/* Header */}
        <header className="sticky top-0 z-10 bg-white border-b border-slate-100 shadow-sm">
          <div className="flex items-center justify-between px-5 py-3 gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition">
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                  {title}
                </h1>
                {breadcrumbs.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    {breadcrumbs.map((crumb, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span>/</span>}
                        {crumb.path
                          ? <Link to={crumb.path} className="hover:text-electric-500 transition-colors">{crumb.label}</Link>
                          : <span className="text-slate-600 font-medium">{crumb.label}</span>}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Global search */}
              {!isDriver && (
                <div className="relative hidden md:block">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input type="text" placeholder="Search drivers, trips…"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    onKeyDown={handleSearch}
                    className="w-56 pl-10 pr-4 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 text-slate-700 placeholder-slate-400 focus:outline-none focus:border-electric-400 focus:bg-white focus:ring-2 focus:ring-electric-400/20 transition-all" />
                </div>
              )}

              {/* Bell */}
              <div className="relative" ref={bellRef}>
                <button onClick={() => setBellOpen(!bellOpen)}
                  className="relative p-2 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition">
                  <Bell className="h-5 w-5" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                </button>
                {bellOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-modal border border-slate-100 py-2 z-50 animate-slide-up">
                    <div className="px-4 py-2.5 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800">Notifications</p>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-xs text-slate-400 text-center">No new notifications</p>
                    </div>
                    <div className="px-4 py-2 border-t border-slate-100">
                      <button className="text-xs font-semibold text-electric-600 hover:text-electric-700 w-full text-center">
                        View All
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-slate-100 transition">
                  <div className="h-8 w-8 rounded-full bg-electric-500 flex items-center justify-center ring-2 ring-electric-100 text-white font-bold text-sm">
                    {(user?.name ?? 'U').charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-modal border border-slate-100 py-2 z-50 animate-slide-up">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800">{user?.name || 'User'}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{user?.email || ''}</p>
                      <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wide text-electric-500 bg-electric-50 px-2 py-0.5 rounded-full">
                        {roleBadge}
                      </span>
                    </div>
                    <button onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition">
                      <User className="w-4 h-4" />My Profile
                    </button>
                    <button onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition">
                      <Settings className="w-4 h-4" />Settings
                    </button>
                    {isSuperAdmin && (
                      <button onClick={() => { navigate('/companies'); setUserMenuOpen(false); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-purple-600 hover:bg-purple-50 transition">
                        <Building2 className="w-4 h-4" />Manage Companies
                      </button>
                    )}
                    <div className="border-t border-slate-100 mt-1 pt-1">
                      <button onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition">
                        <LogOut className="w-4 h-4" />Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
